// Typed wrappers for ALL Phase 3 admin RPCs.
// FORKED — NOT shared with any employee surface. verify-admin-isolation.ts enforces:
//   - no import from src/lib/pulse/**
//   - no import from src/components/employee/**
// All admin reads must funnel through this module so structural denial is auditable.
import { supabase } from '@/lib/supabase';

export type OrgOverview = {
  employee_count: number;
  invite_invited: number;
  invite_accepted: number;
  invite_expired: number;
  last_7d_org_avg: number | null;
  last_7d_n: number | null;
  last_7d_suppressed: boolean;
};

export type PulseDailyRow = {
  day: string;
  avg_pulse: number;
  n_employees: number;
};

export type BandCellRow = {
  band_id: string | null;
  band_label: string | null;
  day: string | null;
  avg_pulse: number | null;
  n_employees: number | null;
  suppressed_cell_count: number;
};

export type InsightRow = {
  id: string;
  theme: string;
  what_phrase: string;
  valence: 'protect' | 'improve';
  excerpt_redacted: string | null;
  k_at_publish: number;
  window_start: string;
  window_end: string;
  where_band_ids: string[] | null;
  status: string;
  created_at: string;
  published_at: string | null;
};

export async function fetchOrgOverview(orgId: string): Promise<OrgOverview> {
  const { data, error } = await supabase.rpc('admin_org_overview', { _org_id: orgId });
  if (error) throw error;
  const row = Array.isArray(data) ? data[0] : data;
  return row as OrgOverview;
}

export async function fetchPulseDaily(orgId: string): Promise<PulseDailyRow[]> {
  const { data, error } = await supabase.rpc('admin_org_pulse_daily', { _org_id: orgId });
  if (error) throw error;
  return (data ?? []) as PulseDailyRow[];
}

export async function fetchPulseByBand(
  orgId: string,
  dimension: 'department' | 'location' | 'level' | 'tenure_years' | 'team',
  from: string,
  to: string,
): Promise<BandCellRow[]> {
  const { data, error } = await supabase.rpc('admin_org_pulse_by_band', {
    _org_id: orgId, _dimension: dimension, _from: from, _to: to,
  });
  if (error) throw error;
  return (data ?? []) as BandCellRow[];
}

export type AnchorDailyRow = {
  day: string;
  anchor_id: string;
  anchor_name: string;
  mean_value: number;
  n: number;
};

export async function fetchOrgAnchorDaily(
  orgId: string,
  from: string,
  to: string,
): Promise<AnchorDailyRow[]> {
  const { data, error } = await supabase.rpc('admin_org_anchor_daily', {
    _org_id: orgId, _from: from, _to: to,
  });
  if (error) throw error;
  return (data ?? []) as AnchorDailyRow[];
}

export type OrgConversationStats = {
  active_employee_count: number;
  total_conversations: number;
  avg_conversations_per_employee: number | null;
  avg_conversation_seconds: number | null;
  suppressed: boolean;
};

export async function fetchOrgConversationStats(orgId: string): Promise<OrgConversationStats> {
  const { data, error } = await supabase.rpc('admin_org_conversation_stats', { _org_id: orgId });
  if (error) throw error;
  const row = Array.isArray(data) ? data[0] : data;
  return row as OrgConversationStats;
}

export async function fetchInsights(
  orgId: string,
  opts: { valence?: 'protect' | 'improve'; theme?: string; bandId?: string; limit?: number } = {},
): Promise<InsightRow[]> {
  const { data, error } = await supabase.rpc('admin_insights', {
    _org_id: orgId,
    _valence: opts.valence ?? null,
    _theme: opts.theme ?? null,
    _band_id: opts.bandId ?? null,
    _limit: opts.limit ?? 50,
  });
  if (error) throw error;
  return (data ?? []) as InsightRow[];
}
